import { useState } from 'react';
import type { NewsItem } from '@jarvis/shared';
import { Icon } from '../components/Icon';
import { Resource } from '../components/Resource';
import { CardHead, DemoBadge, EmptyState, PageHead } from '../components/ui';
import { fmt } from '../lib/format';

const TOPICS = ['Tudo', 'Tecnologia', 'Economia', 'Ciência', 'Brasil', 'Mundo'];

function hostOf(url: string) {
  try { return new URL(url).hostname; } catch { return url; }
}

function Item({ n }: { n: NewsItem }) {
  return (
    <article className="card news-item">
      <div className="news-meta"><span className="badge">{n.topic}</span><DemoBadge /><span>{fmt.relative(n.publishedAt)}</span></div>
      <h3>{n.title}</h3>
      <p className="summary">{n.summary}</p>
      {n.finance && <p className="finance-note"><strong>Contexto, não aconselhamento.</strong> Este resumo descreve o que a fonte relata. Não é recomendação de investimento nem previsão de resultado.</p>}
      <div className="news-meta">
        <a className="source-link" href={n.source.url} target="_blank" rel="noopener noreferrer"><Icon name="external" />{n.source.name}</a>
        <span className="mono">{hostOf(n.source.url)}</span>
        <span>· veículo fictício</span>
      </div>
      <p className="why">Por que você está vendo isto: {n.why}</p>
    </article>
  );
}

function Aside() {
  const [note, setNote] = useState('');
  return (
    <div className="stack">
      <section className="card">
        <CardHead icon="search" title="Pesquisar" />
        <form className="field" onSubmit={(e) => { e.preventDefault(); setNote('A pesquisa na web ainda não está conectada (Etapa 3). Nenhuma consulta foi enviada.'); }}>
          <label htmlFor="researchQ" className="sr-only">Tema da pesquisa</label>
          <input className="input" id="researchQ" placeholder="Ex.: impacto das passkeys" maxLength={200} />
          <button className="btn" type="submit"><Icon name="search" />Pesquisar</button>
        </form>
        {note && <p className="row-sub mt-10" role="status">{note}</p>}
      </section>
      <section className="card">
        <CardHead icon="rss" title="Como as notícias vão funcionar" />
        <ul className="list">
          <li className="row"><div className="row-main"><div className="row-title">Sempre com fonte e link</div><div className="row-sub">Cada resumo aponta para a matéria original e para o veículo.</div></div></li>
          <li className="row"><div className="row-main"><div className="row-title">Fontes escolhidas por você</div><div className="row-sub">Primeiro por feeds RSS que você adicionar; busca na web depois, com limite de uso.</div></div></li>
          <li className="row"><div className="row-main"><div className="row-title">Finanças com cautela</div><div className="row-sub">Contexto e fontes, sem promessas nem recomendações.</div></div></li>
        </ul>
      </section>
    </div>
  );
}

export function News() {
  const [topic, setTopic] = useState('Tudo');
  return (
    <div className="page">
      <PageHead title="Notícias e pesquisas" lead="Resumos com a fonte sempre visível. Os itens abaixo são exemplos fictícios com links para domínios de exemplo." />
      <div className="chips mb-16" role="group" aria-label="Filtrar por tema">
        {TOPICS.map((t) => <button key={t} type="button" className="chip" aria-pressed={t === topic} onClick={() => setTopic(t)}>{t}</button>)}
      </div>
      <div className="news-layout">
        <Resource name="news" skeleton="list" skeletonCount={5}
          empty={{ icon: 'rss', title: 'Nenhuma fonte de notícias configurada', text: 'Quando você adicionar feeds ou ativar a busca, os resumos aparecerão aqui, cada um com link para a fonte original.', actions: [{ label: 'Ver integrações planejadas', href: '#/configuracoes/integracoes', icon: 'plug' }] }}>
          {(items) => {
            const visible = items.filter((n) => topic === 'Tudo' || n.topic === topic);
            return (
              <div className="news-list">
                {visible.map((n) => <Item key={n.id} n={n} />)}
                {visible.length === 0 && <EmptyState icon="news" title="Nada neste tema" text="Nenhuma notícia demonstrativa para este filtro." compact />}
              </div>
            );
          }}
        </Resource>
        <Aside />
      </div>
    </div>
  );
}
